import { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  Collapse,
  collapseClasses,
  FormControl,
  FormGroup,
  FormLabel,
  SxProps,
  Theme,
} from '@mui/material'
import { isEqual, tail } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import { fieldsetBorder } from 'common/styles'
import { useTemplateCustomRender } from 'utils/hook'
import { renderElement } from 'views/Flow/components/FormWizard'

import { Box as BoxProps } from './interface'

const styles: Record<string, SxProps<Theme>> = {
  fieldsetBorder,
  legend: {
    marginLeft: '16px',
    paddingInline: '2px',
  },
  group: {
    columnGap: 1,
    rowGap: 3,
    width: '100%',
    overflow: 'hidden',
  },
  collapseWrap: {
    width: '100%',
    [`.${collapseClasses.wrapperInner}`]: {
      display: 'grid',
      gap: 3,
    },
  },
} as const

export default function Box({
  label,
  border = true,
  children,
  background,
  direction: flexDirection = 'row',
  collapse,
}: BoxProps & DefaultElementProps): JSX.Element {
  const { control } = useFormContext()
  const render = useTemplateCustomRender()

  const collapseValue = useWatch({ control, name: collapse?.element ?? '' })

  const [isCollapse, setCollapse] = useState(false)

  const padding = border ? (label === undefined ? '17px' : '9px 17px 17px') : undefined

  useEffect(() => {
    if (collapse !== undefined) {
      setCollapse(isEqual(collapseValue, collapse.equals))
    }
  }, [collapseValue])

  return (
    <FormControl
      className="box-wrap"
      component="fieldset"
      sx={{
        background,
        width: '100%',
        ...(border ? styles.fieldsetBorder : {}),
      }}
    >
      {label !== undefined && (
        <FormLabel className="box-wrap-label" component="legend" sx={styles.legend}>
          {label}
        </FormLabel>
      )}
      <FormGroup
        sx={{
          ...styles.group,
          padding,
          flexDirection: collapse ? 'column' : flexDirection,
          '& > *': {
            width: '100%',
            maxWidth: '100%',
          },
        }}
      >
        {collapse === undefined ? (
          children?.map((el) => renderElement(el, render))
        ) : (
          <>
            {renderElement(children![0], render)}
            <Collapse sx={styles.collapseWrap} in={isCollapse} unmountOnExit>
              {tail(children).map((el) => renderElement(el, render))}
            </Collapse>
          </>
        )}
      </FormGroup>
    </FormControl>
  )
}
